<template>
  <div class="l-dashboard">
    <page-content
      :has-padd-left-normal="false"
      :has-padd-right-normal="false"
      :has-padd-left-on-act="false"
      :has-padd-right-on-act="false"
      :set-main-col-class="'is-clipped'">
      <div class="columns is-gapless">
        <!-- is-variable is-8 -->
        <todo-list
          class="column is-6-desktop is-5-widescreen is-5-fullhd is-padding"
          :pending-claims="pendingClaims"
          :pending-reservations="pendingReservations"
          :new-tickets="newTickets"
          @updateTicket="updateTicket"
          @updateClaim="updateClaim">
        </todo-list>
        <div class="column">
          <!-- dash-columnScrollY is-padding -->
          <div class="columns is-multiline is-gapless">
            <div
              v-if="canUseCalendar"
              class="column is-12 is-6-widescreen is-padding dash-columnScrollY">
              <dashboard-calendar></dashboard-calendar>
            </div>
            <div class="column is-12 is-6-widescreen is-padding dash-columnScrollY">
              <dashboard-stats
                :total-program="programs.count"
                :total-lot="lots.count"
                :total-account="relations.count"
                :pending-reservations="pendingReservations.length"
                :total-user="users.count"
                :users="users.items"
                :pending-claims="pendingClaims.length"
                :running-options="runningOptions.length"
                :stats-sales="statsSales"
                :stats-sales-reference="statsSalesReference"
                :future-rdvs-count="futureRdvsCount"
                :future-rdvs="futureRdvs"
                :rgpd-stats="rgpdStats"></dashboard-stats>
            </div>
          </div>
        </div>
      </div>
    </page-content>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { bus, EVENTS } from '@/bus'
import moment from 'moment'
import { uniqBy } from 'lodash'

export default {
  name: 'Dashboard',
  components: {
    DashboardStats: () => import('./DashboardStats'),
    TodoList: () => import('./TodoList'),
    DashboardCalendar: () => import('./DashboardCalendar')
  },
  data () {
    return {
      pendingClaims: [],
      pendingReservations: [],
      newTickets: [],
      programs: [],
      lots: [],
      relations: [],
      users: [],
      statsSales: [],
      statsSalesReference: [],
      futureRdvs: [],
      futureRdvsCount: 0,
      // RGPD : compteurs « contacts à supprimer / à anonymiser »
      rgpdStats: { to_delete: 0, to_anonymize: 0 }
    }
  },
  computed: {
    ...mapGetters(['isAdmin', 'isManager', 'isPromoteur', 'runningOptions', 'canUseCalendar', 'statsEnabled', 'accountPk']),
    ...mapState(['currentUser'])
  },
  created () {
    this.initWidget()
    bus.$on(EVENTS.TICKET_CREATED, (data) => {
      this.getTicket(data.resource_pk)
    })
    bus.$on(EVENTS.TICKET_UPDATED, (data) => {
      this.spliceTicket(data.resource_pk)
    })
    bus.$on(EVENTS.RESERVATION_PROPOSAL_SENT, (data) => {
      this.getReservation(data.resource_pk)
    })
    bus.$on(EVENTS.CLAIM_REFUSED, (data) => {
      this.spliceClaim(data.resource_pk)
    })
    bus.$on(EVENTS.CLAIM_APPROVED, (data) => {
      this.spliceClaim(data.resource_pk)
    })
    bus.$on(EVENTS.CLAIM_CREATED, (data) => {
      this.getClaim(data.pk)
    })
  },
  mounted () {
    if (!(this.currentUser.profil === 'admin' && this.currentUser.account.account_type === 'promoteur')) {
      this.$router.replace({ name: 'ProgramCardList' })
    }
  },
  destroyed () {
    bus.$off([EVENTS.TICKET_CREATED, EVENTS.RESERVATION_PROPOSAL_SENT, EVENTS.TICKET_UPDATED, EVENTS.CLAIM_REFUSED, EVENTS.CLAIM_APPROVED, EVENTS.CLAIM_CREATED])
  },
  methods: {
    /**
      * init widget
      * prepare all queries for claim, program, ticket, lot, user
      */
    async initWidget () {
      const programQuery = {
        program__status: 'available',
        per_page: 1,
        cached: 1
      }
      const lotQuery = {
        lot__status: ['available', 'optioned', 'pre_reserved'],
        per_page: 1,
        cached: 1
      }
      const userQuery = { cached: 1 }
      const relationQuery = {
        per_page: 1,
        status: 'accepted',
        cached: 1
      }

      const [
        claim,
        program,
        lot,
        user,
        relation,
        reservation,
        ticket,
        rgpd
      ] = await Promise.allSettled([
        this.$api.get(this.$resources.CLAIM, { status: 'pending' }),
        this.$api.get(this.$resources.STOCK_PROGRAM, programQuery),
        this.$api.get(this.$resources.STOCK_LOT, lotQuery),
        this.$api.get(this.$resources.USER, userQuery),
        this.$api.get(this.$resources.ACCOUNT_RELATION, relationQuery),
        this.$api.get(this.$resources.RESERVATION, { status: 'pending' }),
        this.$api.get(this.$resources.TICKET, { status: ['new'] }),
        this.$api.get('contacts-rgpd-stats')
      ])
      this.pendingClaims = claim.value.data.items
      this.programs = program.value.data
      this.lots = lot.value.data
      this.users = user.value.data
      this.relations = relation.value.data
      this.pendingReservations = reservation.value.data.items
      this.newTickets = ticket.value.data.items
      if (rgpd.status === 'fulfilled') this.rgpdStats = rgpd.value.data
      if (this.canUseCalendar) {
        const futureRdvsQuery = {
          date__gte: moment().toDate(),
          date__lte: moment().endOf('year').toDate(),
          cached: 1
        }
        const { data } = await this.$api.get(this.$resources.RDV, futureRdvsQuery)
        this.futureRdvsCount = data.count
        this.futureRdvs = data.items
      }
      if (this.statsEnabled) {
        const statsReservationQuery = {
          account: this.accountPk,
          signature_date__gte: moment().startOf('year').toDate(),
          signature_date__lte: moment().toDate(),
          status: ['reserved', 'canceled'],
          cached: 1
        }
        const statsSoldQuery = {
          account: this.accountPk,
          sale_date__gte: moment().startOf('year').toDate(),
          sale_date__lte: moment().toDate(),
          status: ['sold'],
          cached: 1
        }
        const statsReferenceQuery = {
          account: this.accountPk,
          pre_reservation_date__gte: moment().startOf('year').subtract(1, 'year').toDate(),
          pre_reservation_date__lte: moment().subtract(1, 'year').toDate(),
          cached: 1
        }
        const [
          statsReservation,
          statsSold,
          statsReference
        ] = await Promise.allSettled([
          this.$statsApi.get('/sales', { params: statsReservationQuery }),
          this.$statsApi.get('/sales', { params: statsSoldQuery }),
          this.$statsApi.get('/sales', { params: statsReferenceQuery })
        ])
        const statsReservationData = statsReservation.status === 'fulfilled' ? statsReservation.value.data : []
        const statsSoldData = statsSold.status === 'fulfilled' ? statsSold.value.data : []
        const statsReferenceData = statsReference.status === 'fulfilled' ? statsReference.value.data : []

        this.statsSales = uniqBy([...statsSoldData, ...statsReservationData], 'ref')
        // this.statsSales = uniqBy([...statsSold.value.data, ...statsReservation.value.data], 'ref')
        this.statsSalesReference = statsReferenceData
      }
    },
    /**
      * get ticket
      * @param {string} - ticket pk
      */
    async getTicket (ticketPk) {
      try {
        const { data } = await this.$api.getOne(this.$resources.TICKET, ticketPk)
        this.newTickets.unshift(data)
      } catch (err) {
        console.log(err)
      }
    },
    /**
      * splice ticket
      * @param {string} - ticket pk
      */
    async spliceTicket (ticketPk) {
      try {
        const { data } = await this.$api.getOne(this.$resources.TICKET, ticketPk)
        const index = this.newTickets.findIndex(x => x.pk === ticketPk)
        this.$set(this.newTickets, index, Object.assign({}, data))
      } catch (err) {
        console.log(err)
      }
    },
    /**
      * splice claim
      * @param {string} - claim pk
      */
    async spliceClaim (claimPk) {
      try {
        let { data } = await this.$api.getOne(this.$resources.CLAIM, claimPk)
        const index = this.pendingClaims.findIndex(x => x.pk === claimPk)
        this.$set(this.newTickets, index, Object.assign({}, data))
      } catch (err) {
        console.log(err)
      }
    },
    /**
      * get reservation
      * @param {string} - reservation pk
      */
    async getReservation (reservationPk) {
      try {
        const { data } = await this.$api.getOne(this.$resources.RESERVATION, reservationPk)
        this.pendingReservations.unshift(data)
      } catch (err) {
        console.log(err)
      }
    },
    /**
      * get claim
      * @param {string} - claim pk
      */
    async getClaim (claimPk) {
      try {
        const { data } = await this.$api.getOne(this.$resources.CLAIM, claimPk)
        this.pendingClaims.unshift(data)
      } catch (err) {
        console.log(err)
      }
    },
    /**
      * update claim
      * @param {string} - claim pk
      * @param {string} - status
      */
    async updateClaim ({ claimPk, status }) {
      const payload = {
        status
      }
      try {
        let { data } = await this.$api.patch(this.$resources.CLAIM, claimPk, payload)
        const index = this.pendingClaims.findIndex(x => x.pk === claimPk)
        this.$set(this.pendingClaims, index, Object.assign({}, data))
      } catch (err) {
        console.log(err)
      }
    },
    /**
      * update ticket
      * @param {string} - ticketPk
      * @param {string} - status
      */
    async updateTicket ({ ticketPk, status }) {
      const payload = {
        status
      }
      try {
        let { data } = await this.$api.patch(this.$resources.TICKET, ticketPk, payload)
        const index = this.newTickets.findIndex(x => x.pk === ticketPk)
        this.$set(this.newTickets, index, Object.assign({}, data))
        this.$router.push({ name: 'TicketVue', params: { id: data.pk } })
      } catch (err) {
        console.log(err)
      }
    }
  }
}
</script>
